import { useCallback } from 'react';

import { PaymentRequestError, RequestPaymentFn, paymentErrorType } from 'features/payment/types';

import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import { useGooglePay } from 'state/google-pay/hooks/use-google-pay';
import { getConfigValue } from 'utils/environment';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';

export const useRequestGooglePayPayment: RequestPaymentFn = ({ googlePay: googlePayInput }) => {
  const { isForPrepaidReload, billingCountry = ISOs.USA, reloadAmount, total } =
    googlePayInput ?? {};

  const { requestGooglePayPayment } = useGooglePay({});

  const requestPayment = useCallback(async () => {
    const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
    const googleConfig = getConfigValue({ key: 'google', defaultValue: {} });
    const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
      transactionType: isForPrepaidReload ? TransactionType.PREPAID : TransactionType.PAYMENT,
      countryCode,
      googleConfig,
    });

    try {
      const googlePayDetails = await requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: isForPrepaidReload ? reloadAmount ?? 0 : total ?? 0,
        gatewayMerchantId,
        gateway,
      });

      if (!googlePayDetails) {
        logger.info({
          isPaymentLog: true,
          message:
            '[useRequestGooglePayPayment][requestPayment] User likely dismissed the Google Pay dialog',
        });
        return undefined;
      }

      return { googlePayDetails };
    } catch (err) {
      logger.error({
        isPaymentLog: true,
        message:
          '[useRequestGooglePayPayment][requestPayment] Error occurred while requesting GooglePay payment.',
        error: err,
      });
      throw new PaymentRequestError(paymentErrorType.googlePayGeneric);
    }
  }, [billingCountry, isForPrepaidReload, reloadAmount, requestGooglePayPayment, total]);

  return { requestPayment };
};
